import { useCallback, useEffect } from 'react';
import type { JSX } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';
import clsx from 'clsx';

import { useConnectivity } from '~/contexts/ConnectivityContext';
import type { UseProjectFilteringReturn } from '~/hooks/useProjectFiltering';
import { useUIStore } from '~/stores/uiStore';

import { Feedback } from '~/components/Feedback';
import { LoadingSpinner } from '~/components/LoadingSpinner';

import type { ProjectProps } from '~/types';

import { ProjectsList } from './List';

interface ProjectsSidebarProps {
  projects: ProjectProps[];
  filtering: UseProjectFilteringReturn;
  selectedProjectId: string | null;
  isLoading?: boolean;
  error?: string | null;
  onProjectSelect: (id: string | null) => void;
  onProjectStatusChange: (id: string, status: string) => void;
  onProjectUpdate: (id: string, updates: Partial<ProjectProps>) => void;
  onProjectDelete: (id: string) => void;
  onGenerateTasksFromPRD: (projectId: string, prdContent: string) => Promise<void>;
}

export function ProjectsSidebar(props: ProjectsSidebarProps): JSX.Element {
  const {
    error,
    filtering,
    isLoading,
    onGenerateTasksFromPRD,
    onProjectDelete,
    onProjectSelect,
    onProjectStatusChange,
    onProjectUpdate,
    projects,
    selectedProjectId,
  } = props;
  const { isSidebarCollapsed, setSidebarCollapsed } = useUIStore();
  const { isOnline } = useConnectivity();

  // Determine if we should show error state vs stale data
  const hasData = projects.length > 0;
  const isStale = !!error && hasData;
  // Only show error UI if there's an error AND no data to show AND we're online
  const shouldShowError = !!error && !hasData && isOnline;

  useEffect(() => {
    const handleResize = (): void => {
      if (window.innerWidth < 1024) {
        // lg breakpoint
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false); // Auto-expand when window is wide enough
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setSidebarCollapsed]);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(!isSidebarCollapsed);
  }, [isSidebarCollapsed, setSidebarCollapsed]);

  // Only show loading spinner on initial load (no data yet)
  if (isLoading && !hasData) {
    return (
      <div className="w-[420px] flex-shrink-0 h-full p-4 bg-surface border-r border-default">
        <LoadingSpinner />
      </div>
    );
  }

  // Only show error if we have NO data to display AND we're online
  // If offline or have stale data, keep showing the UI
  if (shouldShowError) {
    return (
      <div className="w-[420px] flex-shrink-0 h-full p-4 bg-surface border-r border-default">
        <Feedback type="error" title={null} description={error} />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'h-full w-12 flex-shrink-0 flex flex-col bg-surface border-r border-default transition-all duration-300 ease-in-out',
        {
          'w-[420px]': !isSidebarCollapsed,
        },
      )}
    >
      {/* Collapsed: just the expand button */}
      {isSidebarCollapsed && (
        <div className="flex items-center justify-center p-2 shrink-0">
          <Button
            aria-label="Expand sidebar"
            color="default"
            isIconOnly
            onPress={handleToggleSidebar}
            size="sm"
            variant="light"
          >
            <ChevronRightIcon className="h-5 w-5 text-muted" />
          </Button>
        </div>
      )}

      {/* Expanded: collapse button lives inside the List header */}
      {!isSidebarCollapsed && (
        <div className="flex-1 min-h-0">
          <ProjectsList
            filtering={filtering}
            selectedProjectId={selectedProjectId}
            onProjectSelect={onProjectSelect}
            onProjectStatusChange={onProjectStatusChange}
            onProjectUpdate={onProjectUpdate}
            onProjectDelete={onProjectDelete}
            onGenerateTasksFromPRD={onGenerateTasksFromPRD}
            onToggleSidebar={handleToggleSidebar}
            isLoading={isLoading}
            isStale={isStale}
            isOnline={isOnline}
          />
        </div>
      )}
    </div>
  );
}
